import { useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { useDraggableDialog } from '../../hooks/useDraggableDialog';

const WIDTH_CLASS = {
  sm: 'w-[380px]',
  md: 'w-[480px]',
  lg: 'w-[640px]',
};

/**
 * Modal — backwards-compatible rewrite.
 *
 * New API (preferred):
 *   <Modal open={state} onClose={fn} title="..." size="md" footer={<>...</>}>body</Modal>
 *
 * Legacy API (still supported — visibility controlled by mount/unmount):
 *   {showModal && <Modal title="..." onClose={fn} width={720}>body</Modal>}
 *
 * `open` defaults to `true` so legacy callers (which mount the Modal only when visible)
 * keep working. `width` (number) is a fallback when `size` is not supplied; it renders
 * as an inline max-width style.
 *
 * `style` is merged onto the window (e.g. a fixed height), and `bodyClassName`
 * replaces the body's default 'p-5 overflow-y-auto' for a caller that lays out
 * its own fixed header / scrolling body / footer (the record editor, 10/20/26).
 */
export function Modal({
  open = true,
  onClose,
  title,
  size,
  width,
  footer,
  className,
  style,
  bodyClassName = 'p-5 overflow-y-auto',
  children,
}) {
  const dialogRef = useRef(null);
  // Drag the window by its title bar (10/9/26, hooks/useDraggableDialog). A
  // modal without a title marks its own header with data-dialog-handle.
  const drag = useDraggableDialog(open, { ref: dialogRef });
  // Close on the backdrop only when the press STARTED there too (10/8/26):
  // selecting text in a field and letting go outside the box fires a click
  // on the backdrop, which closed the editor and lost what was typed.
  const pressedBackdrop = useRef(false);

  // Keep the latest onClose in a ref so the focus effect depends ONLY on
  // `open`. With onClose in the deps, callers passing an inline arrow (new
  // identity every render) made the effect re-run per parent render — and its
  // dialogRef.focus() stole focus from whatever input the user was typing in
  // (symptom: only the first keystroke registered).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [open]);

  if (!open) return null;

  const widthClass = size ? WIDTH_CLASS[size] : '';
  const inlineStyle = !size && width ? { maxWidth: `${width}px`, width: '100%' } : undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-6 go-fade-in"
      onMouseDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pressedBackdrop.current) onClose?.();
        pressedBackdrop.current = false;
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        style={{ ...inlineStyle, ...style, ...drag.style }}
        onPointerDown={drag.onPointerDown}
        className={clsx(
          'bg-surface rounded-xl shadow-lg overflow-hidden max-h-[90vh] flex flex-col go-pop-in',
          widthClass || (!inlineStyle && 'w-full max-w-[720px]'),
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div data-dialog-handle="" className="px-5 py-4 border-b border-border flex items-center justify-between shrink-0">
            <h4 className="text-h3 m-0 text-gat-greenDarkest">{title}</h4>
            <button
              type="button"
              onClick={onClose}
              className="text-[16px] text-text-body hover:bg-black/5 px-2 py-1 rounded"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}
        <div className={clsx('text-body text-text-dark flex-1', bodyClassName)}>{children}</div>
        {footer && <div className="px-5 py-3 bg-surface-2 flex justify-end gap-2 shrink-0">{footer}</div>}
      </div>
    </div>
  );
}
